/**
 * Manufacture import from one or many .xlsx / .csv / .tsv files (ATL-1701).
 *
 * Wiring only — every decision the import makes lives in utils/manufactureImport/* (mapping,
 * per-file rejection, parsing, grouping, the writer payload, duplicates, the revision warning,
 * preview rows, texts and the report), so it is unit tested. The write path is the ordinary
 * `saveNewManufacture`, one manufacture at a time, which is what makes an imported manufacture
 * identical to one entered on the «Производство» screen.
 *
 * Steps: upload → map columns → options → preview → import → report.
 * Nothing is written before the user confirms the preview.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Platform, ScrollView, StyleSheet, View } from 'react-native'
import { Button, Icon, Spinner, Text, Toggle, useTheme } from '@ui-kitten/components'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import * as DocumentPicker from 'expo-document-picker'
import { Row, Table } from 'react-native-table-component'
import { format } from 'date-fns'

import { UserContext } from '../../global/UserProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { HintView } from '../../components/HintView'
import { currencyFormatterAtom, productsDictAtom, storesDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { getManufacturesOnDateRange, saveNewManufacture } from '../../global/Firebase/firebase'
import { getRevisionsOnDateRange } from '../../global/Firebase/inventory'
import { getLocalStoreDate } from '../../utils/timezoneFunctions'
import { exportToExcel } from '../../utils/exportUtils'
import {
  IMPORT_DOCUMENT_PICKER_TYPES,
  IMPORT_DROPZONE_ACCEPT,
  MalformedImportFileError,
  UnsupportedImportFileError,
  readImportFileFromUri,
  readImportFileFromWeb,
} from '../../utils/importFileReader'
import { parseImportDate } from '../../utils/importCells'
import { createImportBatchId } from '../../utils/salesImport/batchId'
import {
  MANUFACTURE_IMPORT_FIELDS,
  autoMapManufactureColumns,
  prepareManufactureImport,
  sheetHeaders,
  validateManufactureMapping,
} from '../../utils/manufactureImport/files'
import { buildManufactureProductIndex } from '../../utils/manufactureImport/resolve'
import { buildManufactureInfo } from '../../utils/manufactureImport/buildInfo'
import { findDuplicates, revisionWarning } from '../../utils/manufactureImport/duplicates'
import { createRunGuard } from '../../utils/manufactureImport/runGuard'
import { runManufactureImport } from '../../utils/manufactureImport/runImport'
import {
  buildPreviewRows,
  existingRecordsRange,
  formatImportDate,
  lastHandEnteredManufactureDate,
  latestRevisionDate,
  previewCounts,
  selectImportItems,
} from '../../utils/manufactureImport/preview'
import {
  buildReportRows,
  describeError,
  errorKey,
  failureReason,
  fieldKey,
  mappingIssueText,
  refsLocation,
} from '../../utils/manufactureImport/messages'

// Conditional import for web only (same pattern as SalesImportScreen)
const ReactDropzone = Platform.select({
  web: () => require('react-dropzone'),
  default: () => null,
})?.()

const STEPS = {
  UPLOAD: 'upload',
  MAPPING: 'mapping',
  OPTIONS: 'options',
  PREVIEW: 'preview',
  IMPORTING: 'importing',
  DONE: 'done',
}

/** How many manufactures / errors the preview lists before "…and N more" (the report has all). */
const PREVIEW_LIMIT = 50

export default function ManufactureImportScreen({ navigation }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [context] = useContext(UserContext)
  const db = context.db
  const productsDict = useAtomValue(productsDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)

  const [step, setStep] = useState(STEPS.UPLOAD)
  /** ManufactureImportFileInput[]: `{fileName, sheet}` or `{fileName, readError}`, in the order added. */
  const [files, setFiles] = useState([])
  const [isReading, setIsReading] = useState(false)
  /** ManufactureHeaderMapping: field → header name of the first readable file, or null. */
  const [headerMapping, setHeaderMapping] = useState(null)
  const [preparation, setPreparation] = useState(null)
  const [store, setStore] = useState(context.user.store?.id)
  const [includeDuplicates, setIncludeDuplicates] = useState(false)
  /** PreparedManufacture[] built for the preview, with the run's one importBatchId. */
  const [prepared, setPrepared] = useState([])
  /**
   * The preview's records AND the store they were read for. The chooser stays tappable while
   * goToPreview loads, so every preview derivation reads `existing.store`, never the live `store`:
   * comparing one store's records on another store's business day would miss duplicates.
   */
  const [existing, setExisting] = useState({ manufactures: [], revisions: [], range: null, store: null })
  const [isLoadingExisting, setIsLoadingExisting] = useState(false)
  const [progress, setProgress] = useState({ completed: 0, total: 0 })
  const [runResult, setRunResult] = useState(null)
  // Inline errors, not Alert: react-native-web's Alert.alert is a no-op (see SalesImportScreen).
  const [formError, setFormError] = useState(null)
  /** One import run at a time, decided synchronously (utils/manufactureImport/runGuard). */
  const [importGuard] = useState(createRunGuard)

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton onPress={() => navigation.navigate('Home')} iconName='arrow-back-outline' />
        ),
      })
    }
    if (!userRole?.permissions?.['exportImport'] && context.user?.role !== 'admin') {
      navigation.navigate('Home')
    }
  }, [])

  // ---------------------------------------------------------------- file handling

  /** An unreadable file is reported in the run (FILE_UNREADABLE), never an abort of the others. */
  const readFile = async (fileName, read) => {
    try {
      return { fileName, sheet: await read() }
    } catch (error) {
      console.error('Error reading manufacture import file:', fileName, error)
      return {
        fileName,
        readError: error instanceof UnsupportedImportFileError
          ? new Error(t('manufactureImport.unsupportedFile'))
          : error instanceof MalformedImportFileError
            ? new Error(t('importFileMalformedQuotes', { row: error.row }))
            : error,
      }
    }
  }

  const addFiles = (inputs) => {
    setFormError(null)
    setFiles((previous) => [...previous, ...inputs])
  }

  const handleFileSelect = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: IMPORT_DOCUMENT_PICKER_TYPES,
        copyToCacheDirectory: true,
        multiple: true,
      })
      if (result.canceled) return
      setIsReading(true)
      const inputs = []
      for (const asset of result.assets) {
        inputs.push(await readFile(asset.name, () => readImportFileFromUri(asset.uri, asset.name)))
      }
      addFiles(inputs)
    } catch (error) {
      console.error('Manufacture import file picker failed:', error)
      setFormError(t('manufactureImport.importError', { error: error.message }))
    } finally {
      setIsReading(false)
    }
  }

  const onDrop = useCallback(
    async (acceptedFiles, fileRejections) => {
      setIsReading(true)
      try {
        const inputs = []
        for (const file of acceptedFiles || []) {
          inputs.push(await readFile(file.name, () => readImportFileFromWeb(file)))
        }
        // A file failing the dropzone's `accept` filter is listed too, so it reaches the report.
        for (const rejection of fileRejections || []) {
          inputs.push({ fileName: rejection.file.name, readError: new Error(t('manufactureImport.unsupportedFile')) })
        }
        addFiles(inputs)
      } finally {
        setIsReading(false)
      }
    },
    [t]
  )

  const { getRootProps, getInputProps, isDragActive } =
    Platform.OS === 'web'
      ? ReactDropzone.useDropzone({ onDrop, accept: IMPORT_DROPZONE_ACCEPT, multiple: true })
      : { getRootProps: null, getInputProps: null, isDragActive: false }

  /** The mapping is chosen on the first file that has a header row. */
  const firstSheetFile = useMemo(() => files.find((file) => file.sheet && file.sheet.rows.length > 0) ?? null, [files])

  const goToMapping = () => {
    if (!firstSheetFile) {
      setFormError(t('manufactureImport.noReadableFile'))
      return
    }
    setFormError(null)
    setHeaderMapping(autoMapManufactureColumns(sheetHeaders(firstSheetFile.sheet)))
    setStep(STEPS.MAPPING)
  }

  // ---------------------------------------------------------------- column mapping

  const headerOptions = useMemo(() => {
    if (!firstSheetFile) return []
    return [
      ...sheetHeaders(firstSheetFile.sheet)
        .filter((header) => header.trim() !== '')
        .map((header) => ({ text: header, header })),
      { text: t('manufactureImport.ignore'), header: null },
    ]
  }, [firstSheetFile, t])

  const mappingIssues = useMemo(() => (headerMapping ? validateManufactureMapping(headerMapping) : []), [headerMapping])

  const productIndex = useMemo(() => buildManufactureProductIndex(productsDict), [productsDict])

  const goToOptions = () => {
    const result = prepareManufactureImport({ files, mapping: headerMapping, index: productIndex })
    if (result.mappingIssues.length > 0) return
    setPreparation(result)
    setFormError(null)
    setStep(STEPS.OPTIONS)
  }

  // ---------------------------------------------------------------- preview

  const goToPreview = async () => {
    if (!store || !storesDict[store]) {
      setFormError(t(errorKey('STORE_REQUIRED')))
      return
    }
    setFormError(null)
    setIsLoadingExisting(true)
    try {
      // One id per run, stamped on every manufacture of it, so a mistaken run can be found by batch.
      const importBatchId = createImportBatchId()
      const built = preparation.drafts.map((draft) => ({
        key: draft.key,
        draft,
        info: buildManufactureInfo(draft, {
          store,
          organizationId: context.user.organizationId,
          cashierUID: context.user.uid,
          importBatchId,
          productsDict,
        }),
      }))
      const range = existingRecordsRange(
        built.map(({ info }) => info.date),
        new Date()
      )
      const [manufactures, revisions] = range
        ? await Promise.all([
            getManufacturesOnDateRange(db, range, true, store),
            getRevisionsOnDateRange(db, range, true, store),
          ])
        : [[], []]
      setPrepared(built)
      setExisting({ manufactures, revisions, range, store })
      setStep(STEPS.PREVIEW)
    } catch (error) {
      console.error('Manufacture import: preparing the preview failed:', error)
      // buildManufactureInfo refuses with STORE_REQUIRED; a failed read is shown with its message.
      setFormError(
        error?.message === 'STORE_REQUIRED'
          ? failureReason(error.message, t)
          : t('manufactureImport.loadError', { error: error?.message ?? String(error) })
      )
    } finally {
      setIsLoadingExisting(false)
    }
  }

  const keyedInfos = useMemo(() => prepared.map(({ key, info }) => ({ key, info })), [prepared])
  const duplicates = useMemo(
    () => findDuplicates(keyedInfos, existing.manufactures, existing.store, productIndex),
    [keyedInfos, existing.manufactures, existing.store, productIndex]
  )
  const lastRevision = useMemo(() => latestRevisionDate(existing.revisions), [existing.revisions])
  const lastHandEntered = useMemo(() => lastHandEnteredManufactureDate(existing.manufactures), [existing.manufactures])
  // `prepared` carries each draft: a row without a time in the file compares by business day.
  const beforeRevision = useMemo(() => revisionWarning(prepared, lastRevision, existing.store), [prepared, lastRevision, existing.store])
  const previewRows = useMemo(
    () => buildPreviewRows({ prepared, duplicates, beforeRevision, includeDuplicates }),
    [prepared, duplicates, beforeRevision, includeDuplicates]
  )
  const counts = useMemo(() => previewCounts(previewRows), [previewRows])
  const errors = preparation?.errors ?? []

  const formatStoreDate = (date) => format(getLocalStoreDate(existing.store, date), 'dd.MM.yyyy HH:mm')
  const formatStoreDay = (date) => format(getLocalStoreDate(existing.store, date), 'dd.MM.yyyy')

  // ---------------------------------------------------------------- import

  // A second tap before the IMPORTING step renders would write every manufacture twice.
  const startImport = () =>
    importGuard.runExclusive(async () => {
      const items = selectImportItems(prepared, duplicates, includeDuplicates)
      if (items.length === 0) return
      setStep(STEPS.IMPORTING)
      setProgress({ completed: 0, total: items.length })
      try {
        const result = await runManufactureImport({
          items,
          saveManufacture: (info) => saveNewManufacture(db, info),
          onProgress: (completed, total) => setProgress({ completed, total }),
        })
        setRunResult(result)
        setStep(STEPS.DONE)
      } catch (error) {
        console.error('Manufacture import failed:', error)
        setFormError(t('manufactureImport.importError', { error: error.message }))
        setStep(STEPS.PREVIEW)
      }
    })

  const downloadReport = async () => {
    const rows = buildReportRows({ errors, rows: previewRows, failures: runResult?.failures ?? null, t })
    if (rows.length === 0) return
    await exportToExcel(rows, `manufacture-import-report-${Date.now()}`)
  }

  const restart = () => {
    setFiles([])
    setHeaderMapping(null)
    setPreparation(null)
    setIncludeDuplicates(false)
    setPrepared([])
    setExisting({ manufactures: [], revisions: [], range: null })
    setProgress({ completed: 0, total: 0 })
    setRunResult(null)
    setFormError(null)
    setStep(STEPS.UPLOAD)
  }

  // ---------------------------------------------------------------- render helpers

  const UploadIcon = (props) => (
    <Icon
      {...props}
      name='upload-outline'
      width={24}
      height={24}
      fill={isDragActive ? theme['color-info-500'] : theme['text-hint-color']}
    />
  )

  const renderPicker = () => {
    const label = files.length > 0 ? t('manufactureImport.addMoreFiles') : t('manufactureImport.uploadFiles')
    if (Platform.OS === 'web') {
      return (
        <div
          {...getRootProps()}
          style={{
            ...styles.dropzone,
            borderColor: isDragActive ? theme['color-info-500'] : theme['border-basic-color-3'],
            backgroundColor: isDragActive ? theme['color-info-transparent-100'] : theme['background-basic-color-2'],
          }}
        >
          <input {...getInputProps()} />
          <View style={styles.uploadButton}>
            <UploadIcon />
            <Text
              style={{
                ...styles.uploadButtonText,
                color: isDragActive ? theme['color-info-500'] : theme['text-hint-color'],
              }}
            >
              {isDragActive ? t('manufactureImport.dropFilesHere') : label}
            </Text>
          </View>
          <Text style={[styles.dropzoneText, { color: theme['text-hint-color'] }]}>
            {t('manufactureImport.dragAndDropHint')}
          </Text>
        </div>
      )
    }
    return (
      <Button onPress={handleFileSelect} style={{ marginBottom: 20 }}>
        {label}
      </Button>
    )
  }

  const renderUploadStep = () => (
    <View style={{ width: '100%' }}>
      {renderPicker()}
      {isReading && (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
          <Spinner size='small' style={{ marginRight: 10 }} />
          <Text>{t('manufactureImport.readingFiles')}</Text>
        </View>
      )}
      {files.length > 0 && (
        <View style={{ marginBottom: 10 }}>
          <Text category='s1' style={{ marginBottom: 5 }}>
            {t('manufactureImport.filesTitle', { count: files.length })}
          </Text>
          {files.map((file, index) => (
            <Text key={`${file.fileName}-${index}`} style={styles.text} status={file.sheet ? undefined : 'danger'}>
              {file.fileName}
              {' — '}
              {file.sheet
                ? t('manufactureImport.fileRows', { count: Math.max(file.sheet.rows.length - 1, 0) })
                : t('manufactureImport.fileUnreadable', { error: file.readError?.message ?? String(file.readError) })}
            </Text>
          ))}
          <View style={styles.buttonRow}>
            <Button style={{ marginRight: 10 }} disabled={isReading} onPress={goToMapping}>
              {t('manufactureImport.next')}
            </Button>
            <Button appearance='ghost' disabled={isReading} onPress={restart}>
              {t('manufactureImport.clearFiles')}
            </Button>
          </View>
        </View>
      )}
    </View>
  )

  /** The first file's first rows, so the user can check the mapping against real cells. */
  const renderSampleTable = () => {
    const sheet = firstSheetFile.sheet
    const headers = sheetHeaders(sheet)
    const dateColumn = headerMapping?.date === null || !headerMapping ? -1 : headers.indexOf(headerMapping.date)
    const widths = new Array(sheet.rows[0]?.length || 0).fill(150)
    return (
      <ScrollView
        horizontal
        style={styles.tableScroll}
        contentContainerStyle={[styles.tableContainer, { backgroundColor: theme['background-basic-color-1'] }]}
      >
        <Table style={styles.tableView} borderStyle={{ borderWidth: 0 }}>
          {sheet.rows.slice(0, 6).map((rowData, index) => (
            <Row
              key={index}
              // Date cells arrive as Excel serials (46213): render the date column as a date.
              data={rowData.map((cell, column) => {
                if (cell === null) return ''
                if (index > 0 && column === dateColumn) {
                  const parsed = parseImportDate(cell, sheet.date1904)
                  if (parsed) return formatImportDate(parsed)
                }
                return String(cell)
              })}
              widthArr={widths}
              style={styles.row}
              textStyle={index === 0 ? styles.headerText : styles.text}
            />
          ))}
        </Table>
      </ScrollView>
    )
  }

  const renderMappingStep = () => (
    <View style={{ width: '100%' }}>
      <HintView
        text={t('manufactureImport.mapColumnsHint', { file: firstSheetFile.fileName })}
        iconName='alert-circle-outline'
        style={{ marginBottom: 10 }}
      />
      {renderSampleTable()}
      <Text category='h6' style={{ marginTop: 10, marginBottom: 10 }}>
        {t('manufactureImport.mapColumns')}
      </Text>
      {MANUFACTURE_IMPORT_FIELDS.map(({ field, required }) => (
        <View key={field} style={styles.mappingRow}>
          <Text style={styles.mappingLabel}>
            {t(fieldKey(field))}
            {required ? ' *' : ''}
          </Text>
          <PlatformDependantSelect
            data={headerOptions}
            style={styles.select}
            placeholder={t('manufactureImport.selectColumn')}
            selectedOption={headerMapping[field] === null ? undefined : { text: headerMapping[field] }}
            onSelect={(option) => setHeaderMapping((previous) => ({ ...previous, [field]: option.header }))}
          />
        </View>
      ))}
      {mappingIssues.map((issue) => (
        <Text key={`${issue.code}-${issue.field}`} status='danger' style={styles.text}>
          {mappingIssueText(issue, t)}
        </Text>
      ))}
      <View style={styles.buttonRow}>
        <Button appearance='ghost' style={{ marginRight: 10 }} onPress={() => setStep(STEPS.UPLOAD)}>
          {t('manufactureImport.back')}
        </Button>
        <Button disabled={mappingIssues.length > 0} onPress={goToOptions}>
          {t('manufactureImport.next')}
        </Button>
      </View>
    </View>
  )

  const renderOptionsStep = () => (
    <View style={{ width: '100%' }}>
      <Text category='h6' style={{ marginBottom: 10 }}>
        {t('manufactureImport.store')}
      </Text>
      <StoreChooserHorizontal store={store} showAll={false} onChange={setStore} />

      <Text category='h6' style={{ marginTop: 20, marginBottom: 5 }}>
        {t('manufactureImport.options')}
      </Text>
      <HintView
        text={t('manufactureImport.includeDuplicatesHint')}
        iconName='alert-circle-outline'
        style={{ marginBottom: 10 }}
      />
      <Toggle checked={includeDuplicates} onChange={setIncludeDuplicates}>
        {t('manufactureImport.includeDuplicates')}
      </Toggle>

      {isLoadingExisting ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 20 }}>
          <Spinner size='small' style={{ marginRight: 10 }} />
          <Text>{t('manufactureImport.loadingExisting')}</Text>
        </View>
      ) : (
        <View style={styles.buttonRow}>
          <Button appearance='ghost' style={{ marginRight: 10 }} onPress={() => setStep(STEPS.MAPPING)}>
            {t('manufactureImport.back')}
          </Button>
          <Button onPress={goToPreview}>{t('manufactureImport.next')}</Button>
        </View>
      )}
    </View>
  )

  const badges = (row) =>
    [
      row.duplicate && t('manufactureImport.badge.duplicate'),
      !row.willImport && t('manufactureImport.badge.skipped'),
      row.twin && t('manufactureImport.badge.twin'),
      row.beforeRevision && t('manufactureImport.badge.beforeRevision'),
    ].filter(Boolean)

  const renderErrors = () =>
    errors.length > 0 && (
      <View style={{ marginTop: 15 }}>
        <Text category='s1' status='danger'>
          {t('manufactureImport.invalidRows', { count: errors.length })}
        </Text>
        {errors.slice(0, PREVIEW_LIMIT).map((error, index) => (
          <Text key={`${error.ref.fileName}-${error.ref.rowNumber}-${error.code}-${index}`} style={styles.text} appearance='hint'>
            {describeError(error, t)}
          </Text>
        ))}
        {errors.length > PREVIEW_LIMIT && (
          <Text style={styles.text} appearance='hint'>
            {t('manufactureImport.andMore', { count: errors.length - PREVIEW_LIMIT })}
          </Text>
        )}
      </View>
    )

  const renderPreviewStep = () => (
    <View style={{ width: '100%' }}>
      <Text category='h6' style={{ marginBottom: 10 }}>
        {t('manufactureImport.summary')}
      </Text>
      <View style={[styles.summaryCard, { backgroundColor: theme['background-basic-color-1'] }]}>
        <Text category='h5' testID='manufacture-import-to-import'>
          {t('manufactureImport.summaryToImport', { count: counts.toImport })}
        </Text>
        <Text category='s1' style={{ marginTop: 2 }}>
          {t('manufactureImport.summaryTotalCost', { total: currencyFormatter.format(counts.totalSum) })}
        </Text>
        <Text appearance='hint' style={{ marginTop: 6 }}>
          {t('manufactureImport.summaryFiles', {
            accepted: preparation.acceptedFiles.length,
            rejected: preparation.rejectedFiles.length,
          })}
        </Text>
        <Text appearance='hint'>{t('manufactureImport.summaryPrepared', { count: counts.prepared })}</Text>
        {counts.duplicates > 0 && (
          <Text appearance='hint'>
            {includeDuplicates
              ? t('manufactureImport.summaryDuplicates', { count: counts.duplicates })
              : t('manufactureImport.summarySkippedDuplicates', { count: counts.skippedDuplicates })}
          </Text>
        )}
        {counts.twins > 0 && <Text appearance='hint'>{t('manufactureImport.summaryTwins', { count: counts.twins })}</Text>}
      </View>

      <View style={{ marginTop: 10 }}>
        <Text style={styles.text}>
          {lastHandEntered
            ? t('manufactureImport.lastHandEntered', { date: formatStoreDate(lastHandEntered) })
            : existing.range && t('manufactureImport.lastHandEnteredNone', { date: formatStoreDay(existing.range.startDate) })}
        </Text>
        <Text style={styles.text}>
          {lastRevision
            ? t('manufactureImport.lastRevision', { date: formatStoreDate(lastRevision) })
            : existing.range && t('manufactureImport.lastRevisionNone', { date: formatStoreDay(existing.range.startDate) })}
        </Text>
        <Text style={styles.text} appearance='hint'>
          {t('manufactureImport.costPriceNote')}
        </Text>
      </View>

      {counts.beforeRevision > 0 && (
        <Text status='warning' style={{ marginTop: 10 }}>
          {t('manufactureImport.revisionWarning', { count: counts.beforeRevision })}
        </Text>
      )}

      {previewRows.length > 0 && (
        <View style={{ marginTop: 15 }}>
          <Text category='s1' style={{ marginBottom: 5 }}>
            {t('manufactureImport.manufacturesPreview')}
          </Text>
          {previewRows.slice(0, PREVIEW_LIMIT).map((row) => (
            <View key={row.key} style={styles.manufactureRow}>
              <Text appearance={row.willImport ? 'default' : 'hint'}>
                {row.date}
                {'  ·  '}
                {row.productName}
                {'  ·  '}
                {t('manufactureImport.rowQuantity', { quantity: row.quantity })}
                {row.batchNumber ? `  ·  ${t('manufactureImport.rowBatch', { batch: row.batchNumber })}` : ''}
              </Text>
              <Text appearance='hint' style={styles.text}>
                {refsLocation(row.refs)}
                {'  ·  '}
                {t('manufactureImport.rowIngredients', { count: row.ingredientCount })}
                {'  ·  '}
                {t('manufactureImport.rowCost', { total: currencyFormatter.format(row.totalSum) })}
              </Text>
              {badges(row).length > 0 && (
                <Text status='warning' style={styles.text}>
                  {badges(row).join(' · ')}
                </Text>
              )}
            </View>
          ))}
          {previewRows.length > PREVIEW_LIMIT && (
            <Text style={styles.text} appearance='hint'>
              {t('manufactureImport.andMore', { count: previewRows.length - PREVIEW_LIMIT })}
            </Text>
          )}
        </View>
      )}

      {renderErrors()}

      <Button appearance='ghost' style={{ alignSelf: 'flex-start', marginTop: 10 }} onPress={downloadReport}>
        {t('manufactureImport.downloadReport')}
      </Button>

      <View style={styles.buttonRow}>
        <Button appearance='ghost' style={{ marginRight: 10 }} onPress={() => setStep(STEPS.OPTIONS)}>
          {t('manufactureImport.back')}
        </Button>
        <Button
          testID='manufacture-import-submit'
          accessibilityRole='button'
          disabled={counts.toImport === 0}
          onPress={startImport}
        >
          {t('manufactureImport.import', { count: counts.toImport })}
        </Button>
      </View>
    </View>
  )

  const renderDoneStep = () => (
    <View style={{ width: '100%' }}>
      <Text category='h6' style={{ marginBottom: 10 }}>
        {t('manufactureImport.done')}
      </Text>
      <Text style={styles.text} testID='manufacture-import-created'>
        {t('manufactureImport.doneCreated', { count: runResult.created })}
      </Text>
      <Text style={styles.text}>{t('manufactureImport.doneSkipped', { count: counts.skippedDuplicates })}</Text>
      {errors.length > 0 && <Text style={styles.text}>{t('manufactureImport.doneErrors', { count: errors.length })}</Text>}
      {runResult.failures.length > 0 && (
        <View style={{ marginTop: 10 }}>
          <Text category='s1' status='danger'>
            {t('manufactureImport.doneFailed', { count: runResult.failures.length })}
          </Text>
          {runResult.failures.slice(0, PREVIEW_LIMIT).map((failure) => (
            <Text key={failure.key} style={styles.text} appearance='hint'>
              {refsLocation(failure.refs)}
              {' — '}
              {failureReason(failure.message, t)}
            </Text>
          ))}
          {runResult.failures.length > PREVIEW_LIMIT && (
            <Text style={styles.text} appearance='hint'>
              {t('manufactureImport.andMore', { count: runResult.failures.length - PREVIEW_LIMIT })}
            </Text>
          )}
        </View>
      )}
      <Button appearance='ghost' style={{ alignSelf: 'flex-start', marginTop: 10 }} onPress={downloadReport}>
        {t('manufactureImport.downloadReport')}
      </Button>
      <Button style={{ marginTop: 20 }} onPress={restart}>
        {t('manufactureImport.importMore')}
      </Button>
    </View>
  )

  // ---------------------------------------------------------------- render

  return (
    <ScrollView style={{ padding: 20 }} contentContainerStyle={styles.pageContent}>
      <Text category='h3' style={{ marginBottom: 5 }}>
        {t('manufactureImport.title')}
      </Text>
      {step === STEPS.UPLOAD && (
        <HintView text={t('manufactureImport.hint')} iconName='alert-circle-outline' style={{ marginBottom: 10 }} />
      )}

      {formError && (
        <Text status='danger' testID='manufacture-import-error' style={{ marginBottom: 10 }}>
          {formError}
        </Text>
      )}

      {step === STEPS.UPLOAD && renderUploadStep()}
      {step === STEPS.MAPPING && firstSheetFile && headerMapping && renderMappingStep()}
      {step === STEPS.OPTIONS && preparation && renderOptionsStep()}
      {step === STEPS.PREVIEW && preparation && renderPreviewStep()}

      {step === STEPS.IMPORTING && (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 20 }}>
          <Spinner size='small' style={{ marginRight: 10 }} />
          <Text>
            {t('manufactureImport.importProgress', { count: progress.completed, totalCount: progress.total })}
          </Text>
        </View>
      )}

      {step === STEPS.DONE && runResult && renderDoneStep()}

      <View style={{ height: 50 }} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  pageContent: { alignItems: 'stretch', maxWidth: 820, width: '100%' },
  row: {
    minHeight: 48,
    borderBottomWidth: 1,
    borderBottomColor: 'gray',
    flexDirection: 'row',
    alignItems: 'center',
  },
  text: {
    fontSize: Platform.OS === 'web' ? 14 : 16,
    marginRight: 10,
  },
  headerText: {
    fontWeight: 'bold',
    fontSize: Platform.OS === 'web' ? 14 : 16,
    marginRight: 10,
  },
  mappingRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  mappingLabel: { width: 210 },
  select: { width: 220, marginLeft: 10 },
  buttonRow: { flexDirection: 'row', alignItems: 'center', marginTop: 20 },
  summaryCard: {
    alignSelf: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    minWidth: 260,
  },
  manufactureRow: { marginBottom: 10 },
  dropzone: {
    alignSelf: 'stretch',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 8,
    padding: 20,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
  },
  uploadButtonText: {
    marginLeft: 10,
    fontSize: 16,
  },
  dropzoneText: {
    marginTop: 10,
    fontSize: 14,
  },
  tableView: {
    marginBottom: 10,
    width: '100%',
  },
  tableContainer: {
    flexGrow: 0,
    marginBottom: 10,
    borderRadius: 10,
    paddingBottom: 10,
    paddingHorizontal: 10,
  },
  tableScroll: {
    width: '100%',
  },
})
